import { Show, createSignal, onCleanup, onMount, type Component, For } from "solid-js";
import { PanelLayout, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { PanelBadge, type Intent } from "@/shared/panel-primitives";
import { registerPanel } from "@/panel-registry";
import "./firewall.css";

type Direction = "Any" | "Inbound" | "Outbound";
type EnabledFilter = "Any" | "True" | "False";

const DIRECTIONS: Direction[] = ["Any", "Inbound", "Outbound"];
const ENABLED_VALUES: EnabledFilter[] = ["Any", "True", "False"];

// Valeurs acceptees par le tool backend firewall_add_rule.
const FORM_DIRECTIONS = ["Inbound", "Outbound"] as const;
const FORM_ACTIONS = ["Allow", "Block"] as const;
const FORM_PROTOCOLS = ["TCP", "UDP", "Any"] as const;

interface FirewallRule {
  Name?: string;
  DisplayName?: string;
  Direction?: string;
  Action?: string;
  Enabled?: string | boolean;
  Profile?: string;
  Protocol?: string;
  LocalPort?: string;
  RemoteAddress?: string;
  RemotePort?: string;
}

interface ListResult {
  rows?: FirewallRule[];
}

// Etat du formulaire de creation / clonage de regle.
interface RuleForm {
  displayName: string;
  direction: string;
  action: string;
  protocol: string;
  localPort: string;
  remoteAddress: string;
}

function normalizeDirection(v: unknown): string {
  return /out/i.test(String(v ?? "")) ? "Outbound" : "Inbound";
}

function normalizeAction(v: unknown): string {
  return /block|deny/i.test(String(v ?? "")) ? "Block" : "Allow";
}

function normalizeProtocol(v: unknown): string {
  const s = String(v ?? "")
    .trim()
    .toUpperCase();
  if (s === "UDP" || s === "17") return "UDP";
  if (s === "TCP" || s === "6") return "TCP";
  return s === "ANY" || s === "" ? "Any" : "Any";
}

function normalizeRule(row: FirewallRule & Record<string, unknown>): FirewallRule {
  return {
    Name: String(row.Name ?? row["name"] ?? ""),
    DisplayName: String(row.DisplayName ?? row["displayName"] ?? row.Name ?? row["name"] ?? ""),
    Direction: String(row.Direction ?? row["direction"] ?? ""),
    Action: String(row.Action ?? row["action"] ?? ""),
    Enabled: (row.Enabled ?? row["enabled"]) as string | boolean | undefined,
    Profile: String(row.Profile ?? row["profile"] ?? ""),
    Protocol: String(row.Protocol ?? row["protocol"] ?? ""),
    LocalPort: String(row.LocalPort ?? row["localPort"] ?? ""),
    RemotePort: String(row.RemotePort ?? row["remotePort"] ?? ""),
    RemoteAddress: String(row.RemoteAddress ?? row["remoteAddress"] ?? ""),
  };
}

function listParams(direction: Direction, enabled: EnabledFilter): Record<string, unknown> {
  const params: Record<string, unknown> = { limit: 500 };
  if (direction !== "Any") params.direction = direction;
  if (enabled === "True") params.enabled = true;
  if (enabled === "False") params.enabled = false;
  return params;
}

const FirewallPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [direction, setDirection] = createSignal<Direction>("Any");
  const [enabledF, setEnabledF] = createSignal<EnabledFilter>("Any");
  const [filter, setFilter] = createSignal("");
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  const [modal, setModal] = createSignal<{ title: string; body: string } | null>(null);
  const [selected, setSelected] = createSignal<FirewallRule | null>(null);
  const [form, setForm] = createSignal<RuleForm | null>(null);
  const [creating, setCreating] = createSignal(false);

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const listQuery = createToolQuery<ListResult>(() => ({
    tool: "firewall_list_rules",
    params: listParams(direction(), enabledF()),
    enabled: enabled(),
    refetchInterval: 0,
  }));

  function applyFilter(rows: FirewallRule[]): FirewallRule[] {
    const q = filter().trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const dn = String(r.DisplayName || "").toLowerCase();
      const nm = String(r.Name || "").toLowerCase();
      return dn.includes(q) || nm.includes(q);
    });
  }

  function isRuleEnabled(r: FirewallRule): boolean {
    return /true|enabled/i.test(String(r.Enabled || ""));
  }

  // ── Clonage de regle ──────────────────────────────────────────────────────
  // Pre-remplit le formulaire de creation avec les valeurs d'une regle
  // existante. Le nom recoit un suffixe " (copie)" pour eviter le conflit.
  function cloneRule(rule: FirewallRule): void {
    const base = String(rule.DisplayName || rule.Name || "Regle");
    setForm({
      displayName: `${base} (copie)`,
      direction: normalizeDirection(rule.Direction),
      action: normalizeAction(rule.Action),
      protocol: normalizeProtocol(rule.Protocol),
      localPort: String(rule.LocalPort ?? "").replace(/^any$/i, ""),
      remoteAddress: String(rule.RemoteAddress ?? "").replace(/^any$/i, ""),
    });
    setActionStatus({ text: `Regle "${base}" clonee — ajustez puis creez.`, level: "info" });
  }

  function patchForm(patch: Partial<RuleForm>): void {
    const cur = form();
    if (cur) setForm({ ...cur, ...patch });
  }

  async function submitForm(): Promise<void> {
    const f = form();
    if (!f || creating()) return;
    const name = f.displayName.trim();
    if (!name) {
      setActionStatus({ text: "Le nom de la regle est requis.", level: "warn" });
      return;
    }
    const ok = await confirm.confirm({
      title: "Creer une regle pare-feu",
      body: `Creer la regle "${name}" — ${f.direction} ${f.action} ${f.protocol}${
        f.localPort.trim() ? `:${f.localPort.trim()}` : ""
      } ?`,
      level: "warn",
      confirmLabel: "Creer",
    });
    if (!ok) return;
    setCreating(true);
    setActionStatus({ text: `Creation de "${name}"...`, level: "info" });
    try {
      const params: Record<string, unknown> = {
        displayName: name,
        direction: f.direction,
        action: f.action,
        protocol: f.protocol,
      };
      if (f.localPort.trim()) params.localPort = f.localPort.trim();
      if (f.remoteAddress.trim()) params.remoteAddress = f.remoteAddress.trim();
      await tools.call("firewall_add_rule", params, { userConfirmedAt: new Date().toISOString() });
      setActionStatus({ text: `Regle "${name}" : creation lancee.`, level: "info" });
      setForm(null);
      // Le tool est un job async cote agent — laisse le temps a la regle
      // d'apparaitre avant de rafraichir la liste.
      setTimeout(() => void listQuery.refetch(), 1500);
    } catch (err) {
      setActionStatus({
        text: `Erreur creation: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    } finally {
      setCreating(false);
    }
  }

  // Ctrl+D : clone la regle selectionnee.
  function onKeyDown(e: KeyboardEvent): void {
    if ((e.ctrlKey || e.metaKey) && (e.key === "d" || e.key === "D")) {
      const sel = selected();
      if (sel) {
        e.preventDefault();
        cloneRule(sel);
      }
    }
  }

  onMount(() => window.addEventListener("keydown", onKeyDown));
  onCleanup(() => window.removeEventListener("keydown", onKeyDown));

  async function showDetail(rule: FirewallRule): Promise<void> {
    const name = String(rule.Name || "");
    if (!name) return;
    try {
      const body = JSON.stringify(rule, null, 2);
      setModal({ title: `Detail regle ${name}`, body });
      setActionStatus({ text: `Detail local pour ${name}.`, level: "info" });
    } catch (err) {
      setActionStatus({
        text: `Erreur detail: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function runAction(name: string, displayName: string, action: "enable" | "disable" | "delete"): Promise<void> {
    if (!name) return;
    const labels: Record<string, string> = {
      enable: "Enable",
      disable: "Disable",
      delete: "Delete",
    };
    const levels: Record<string, "info" | "warn" | "danger"> = {
      enable: "warn",
      disable: "warn",
      delete: "danger",
    };
    const label = labels[action];
    const level = levels[action];
    const ok = await confirm.confirm({
      title: `${label} - ${displayName || name}`,
      body: `Confirmer l'action "${label}" sur la regle pare-feu ${displayName || name} ?`,
      level,
      confirmLabel: label,
      entityName: level === "danger" ? displayName || name : "",
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label} sur ${displayName || name}...`, level: "info" });
    try {
      const tool =
        action === "enable"
          ? "firewall_enable_rule"
          : action === "disable"
            ? "firewall_disable_rule"
            : "firewall_remove_rule";
      await tools.call(tool, { name }, { userConfirmedAt: new Date().toISOString() });
      setActionStatus({ text: `Action ${label} sur ${name} reussie.`, level: "info" });
      await listQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  const columns: Column<FirewallRule>[] = [
    { accessorKey: "DisplayName", header: "DisplayName" },
    { accessorKey: "Direction", header: "Direction" },
    {
      accessorKey: "Action",
      header: "Action",
      cell: (info) => {
        const v = String(info.getValue() ?? "");
        const lvl: Intent = /allow/i.test(v) ? "ok" : /block|deny/i.test(v) ? "danger" : "muted";
        return <PanelBadge intent={lvl}>{v}</PanelBadge>;
      },
    },
    {
      accessorKey: "Enabled",
      header: "Enabled",
      cell: (info) => {
        const v = /true|enabled/i.test(String(info.getValue() ?? ""));
        return <PanelBadge intent={v ? "ok" : "danger"}>{v ? "True" : "False"}</PanelBadge>;
      },
    },
    { accessorKey: "Profile", header: "Profile" },
  ];

  const toolbar = (
    <>
      <select
        class="panel-filter-input panel-filter-input--narrow"
        title="Direction"
        value={direction()}
        onChange={(e) => setDirection(e.currentTarget.value as Direction)}
      >
        <For each={DIRECTIONS}>{(d) => <option value={d}>{d}</option>}</For>
      </select>
      <select
        class="panel-filter-input panel-filter-input--narrow"
        title="Enabled"
        value={enabledF()}
        onChange={(e) => setEnabledF(e.currentTarget.value as EnabledFilter)}
      >
        <For each={ENABLED_VALUES}>{(v) => <option value={v}>{v}</option>}</For>
      </select>
      <input
        type="text"
        class="panel-filter-input"
        placeholder="Filtre (nom partiel)..."
        spellcheck={false}
        value={filter()}
        onInput={(e) => setFilter(e.currentTarget.value)}
      />
      <button
        type="button"
        class="btn btn--xs btn--ghost"
        disabled={!selected()}
        title="Dupliquer la regle selectionnee (Ctrl+D)"
        onClick={() => {
          const sel = selected();
          if (sel) cloneRule(sel);
        }}
      >
        Dupliquer
      </button>
      <button type="button" class="btn btn--xs btn--info" onClick={() => listQuery.refetch()}>
        Rafraichir
      </button>
    </>
  );

  const statusBar = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (listQuery.isFetching) return { text: "Chargement...", level: "info" };
    if (listQuery.error)
      return {
        text: `Erreur: ${listQuery.error instanceof Error ? listQuery.error.message : String(listQuery.error)}`,
        level: "danger",
      };
    return undefined;
  };

  return (
    <>
      <PanelLayout title="Pare-feu Windows" toolbar={toolbar} status={statusBar()}>
        <Show when={selected()}>
          {(sel) => (
            <div class="fw-selection">
              <span class="fw-selection-label">Selection :</span>
              <strong>{sel().DisplayName || sel().Name}</strong>
              <span class="fw-selection-hint">Ctrl+D pour dupliquer</span>
            </div>
          )}
        </Show>

        <Show when={form()}>
          {(f) => (
            <div class="fw-form">
              <div class="fw-form-header">
                <strong>Nouvelle regle pare-feu</strong>
                <button type="button" class="btn btn--xs btn--ghost" onClick={() => setForm(null)}>
                  Annuler
                </button>
              </div>
              <div class="fw-form-grid">
                <label class="fw-field fw-field--wide">
                  <span>DisplayName</span>
                  <input
                    type="text"
                    class="panel-filter-input"
                    spellcheck={false}
                    value={f().displayName}
                    onInput={(e) => patchForm({ displayName: e.currentTarget.value })}
                  />
                </label>
                <label class="fw-field">
                  <span>Direction</span>
                  <select
                    class="panel-filter-input"
                    value={f().direction}
                    onChange={(e) => patchForm({ direction: e.currentTarget.value })}
                  >
                    <For each={FORM_DIRECTIONS}>{(d) => <option value={d}>{d}</option>}</For>
                  </select>
                </label>
                <label class="fw-field">
                  <span>Action</span>
                  <select
                    class="panel-filter-input"
                    value={f().action}
                    onChange={(e) => patchForm({ action: e.currentTarget.value })}
                  >
                    <For each={FORM_ACTIONS}>{(a) => <option value={a}>{a}</option>}</For>
                  </select>
                </label>
                <label class="fw-field">
                  <span>Protocole</span>
                  <select
                    class="panel-filter-input"
                    value={f().protocol}
                    onChange={(e) => patchForm({ protocol: e.currentTarget.value })}
                  >
                    <For each={FORM_PROTOCOLS}>{(p) => <option value={p}>{p}</option>}</For>
                  </select>
                </label>
                <label class="fw-field">
                  <span>Port local</span>
                  <input
                    type="text"
                    class="panel-filter-input"
                    placeholder="ex: 443 (vide = Any)"
                    spellcheck={false}
                    value={f().localPort}
                    onInput={(e) => patchForm({ localPort: e.currentTarget.value })}
                  />
                </label>
                <label class="fw-field">
                  <span>Adresse distante</span>
                  <input
                    type="text"
                    class="panel-filter-input"
                    placeholder="ex: 10.0.0.0/8 (vide = Any)"
                    spellcheck={false}
                    value={f().remoteAddress}
                    onInput={(e) => patchForm({ remoteAddress: e.currentTarget.value })}
                  />
                </label>
              </div>
              <div class="fw-form-actions">
                <button
                  type="button"
                  class="btn btn--xs btn--primary"
                  disabled={creating() || !f().displayName.trim()}
                  onClick={() => void submitForm()}
                >
                  {creating() ? "Creation..." : "Creer la regle"}
                </button>
              </div>
            </div>
          )}
        </Show>

        <DataTable<FirewallRule>
          rows={applyFilter(
            (listQuery.data?.rows ?? []).map((r) => normalizeRule(r as FirewallRule & Record<string, unknown>)),
          )}
          columns={columns}
          emptyMessage="Aucune regle."
          onRowSelect={(r) => setSelected(r)}
          rowActions={(r) => {
            const name = String(r.Name || "");
            const display = String(r.DisplayName || "");
            const en = isRuleEnabled(r);
            return (
              <>
                <button type="button" class="btn btn--xs btn--info" onClick={() => showDetail(r)}>
                  Voir details
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--ghost"
                  title="Dupliquer cette regle"
                  onClick={() => cloneRule(r)}
                >
                  Dupliquer
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--ghost"
                  disabled={en}
                  onClick={() => runAction(name, display, "enable")}
                >
                  Enable
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--warn"
                  disabled={!en}
                  onClick={() => runAction(name, display, "disable")}
                >
                  Disable
                </button>
                <button
                  type="button"
                  class="btn btn--xs btn--danger"
                  onClick={() => runAction(name, display, "delete")}
                >
                  Delete
                </button>
              </>
            );
          }}
        />
      </PanelLayout>
      <Show when={modal()}>
        {(m) => (
          <div
            class="panel-modal-overlay"
            style={{
              position: "fixed",
              inset: "0",
              background: "rgba(0,0,0,0.45)",
              display: "flex",
              "align-items": "center",
              "justify-content": "center",
              "z-index": "9999",
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) setModal(null);
            }}
          >
            <div
              class="panel-modal"
              style={{
                background: "#1f2128",
                color: "#e7e7e7",
                "max-width": "80vw",
                "max-height": "80vh",
                width: "720px",
                "border-radius": "8px",
                display: "flex",
                "flex-direction": "column",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "0.75em 1em",
                  "border-bottom": "1px solid #333",
                  display: "flex",
                  "justify-content": "space-between",
                  "align-items": "center",
                }}
              >
                <strong>{m().title}</strong>
                <button type="button" class="btn btn--xs btn--ghost" onClick={() => setModal(null)}>
                  Fermer
                </button>
              </div>
              <pre
                style={{
                  margin: "0",
                  padding: "1em",
                  overflow: "auto",
                  "font-size": "12px",
                  "white-space": "pre-wrap",
                  "word-break": "break-all",
                  "max-height": "60vh",
                }}
              >
                {m().body}
              </pre>
            </div>
          </div>
        )}
      </Show>
    </>
  );
};

registerPanel("firewall", FirewallPanel);
export default FirewallPanel;
